<template>
  <div>
    <!-- TABLE HISTORIQUE -->
    <div class="content">
      <div class="block">
        <div class="block-content block-content-full">
          <table class="table table-striped table-vcenter table-bordered">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Montant</th>
                <th class="text-center">Actions</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="invoice in invoices" :key="invoice.id">
                <td>{{ formatDate(invoice.created_at) }}</td>

                <td>
                  <span class="badge bg-primary rounded-pill">
                    {{ getLabel(invoice) }}
                  </span>
                </td>

                <td :class="{'text-danger': invoice.amount < 0}">
                  {{ formatPrice(invoice.amount) }}
                </td>

                <td class="text-center">
                  <v-btn
                    v-if="canCorrectInvoice(invoice)"
                    small
                    outlined
                    color="warning"
                    @click="openDialog(invoice)"
                  >
                    Erreur de saisie
                  </v-btn>
                </td>
              </tr>

              <tr v-if="!invoices.length">
                <td colspan="4" class="text-center text-muted">
                  Aucun paiement
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- DIALOG -->
    <v-dialog
      v-model="dialog"
      max-width="650"
      persistent
      overlay-color="black"
      overlay-opacity="0.5"
      transition="invoice-cancel-slide-y-transition"
    >
      <v-card class="invoice-cancel-modal">
        <div class="modal-header invoice-cancel-modal__header">
          <h5 class="modal-title mb-0">
            Correction d'erreur de saisie
          </h5>
          <button type="button" class="btn-close" :disabled="loading" @click="closeDialog"></button>
        </div>

        <div v-if="selected" class="modal-body invoice-cancel-modal__body">
          <div class="invoice-cancel-modal__summary mb-3">
            <div>
              <div class="text-muted small">Paiement original</div>
              <div class="fw-semibold">
                {{ selected.payment_method?.name || 'Non spécifié' }} — {{ formatPrice(selected.amount) }}
              </div>
              <div class="text-muted small">
                {{ formatDate(selected.created_at) }}
              </div>
            </div>
            <span class="badge bg-warning-light text-warning rounded-pill">
              Correction
            </span>
          </div>

          <div class="alert alert-warning mb-3">
            Cette correction annule le paiement saisi et recrée un mouvement avec la méthode et le montant corrigés.
          </div>

          <!-- Nouvelle méthode -->
          <label class="form-label fw-semibold">Nouvelle méthode de paiement</label>

          <v-select
            v-model="selectedMethod"
            :items="methods"
            item-text="name"
            item-value="id"
            label="Méthode"
            outlined
            dense
            prepend-inner-icon="mdi-credit-card"
            :loading="methodsLoading"
            :rules="[rules.required]"
            class="mt-2"
          />

          <!-- Montant -->
          <label class="form-label fw-semibold mt-1">Montant corrigé</label>
          <v-text-field
            v-model="amount"
            type="number"
            step="0.01"
            min="0.01"
            :max="selected.amount"
            outlined
            dense
            suffix="€"
            class="mt-3"
            :rules="[rules.required, rules.float, rules.positive, rules.maxSelectedAmount]"
          />

          <!-- Note -->
          <label class="form-label fw-semibold mt-1">Explication</label>
          <v-textarea
            v-model="note"
            placeholder="Exemple : erreur de saisie du montant encaissé"
            outlined
            dense
            rows="3"
            counter="500"
            class="mt-3"
            :rules="[v => !v || v.length <= 500 || 'Maximum 500 caractères']"
          />

          <div v-if="apiError" class="alert alert-danger mt-3 mb-0">
            {{ apiError }}
          </div>
        </div>

        <div class="modal-footer invoice-cancel-modal__footer">
          <button type="button" class="btn btn-alt-secondary" :disabled="loading" @click="closeDialog">
            Annuler
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!canSubmit || loading"
            @click="submit"
          >
            <span v-if="loading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            Confirmer
          </button>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios"
import moment from "moment"
import "moment/locale/fr"

export default {
  props: {
    invoices: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      dialog: false,
      selected: null,

      methods: [],
      methodsLoading: false,
      selectedMethod: null,

      amount: null,
      note: "",

      loading: false,
      apiError: null,

      rules: {
        required: v => !!v || "Champ requis",
        float: v => {
          const n = parseFloat(v)
          return !isNaN(n) || "Montant invalide"
        },
        positive: v => parseFloat(v) > 0 || "Le montant doit être supérieur à 0",
        maxSelectedAmount: v => {
          const max = parseFloat(this.selected?.amount || 0)
          return parseFloat(v) <= max || `Maximum ${max.toFixed(2)} €`
        },
      }
    }
  },

  computed: {
    canSubmit() {
      return (
        this.selected &&
        this.selectedMethod &&
        this.amount !== null &&
        !isNaN(parseFloat(this.amount)) &&
        parseFloat(this.amount) > 0 &&
        parseFloat(this.amount) <= parseFloat(this.selected.amount)
      )
    }
  },

  methods: {
    canCorrectInvoice(invoice) {
      return parseFloat(invoice.amount || 0) > 0
        && !invoice.virtual_fidelity_points
        && invoice.payment_method?.name !== 'Points de fidélité'
        && !this.hasStripeTransaction(invoice)
    },

    hasStripeTransaction(invoice) {
      return String(invoice.payment_method?.name || '').trim().toLowerCase() === 'stripe'
        || !!invoice.payment_intent_id
    },

    async openDialog(invoice) {
      this.selected = invoice
      this.amount = invoice.amount
      this.note = ""
      this.apiError = null
      this.dialog = true

      await this.loadMethods()

      this.selectedMethod = invoice.payment_method_id
      console.log(invoice)
    },

    closeDialog() {
      this.dialog = false
      this.selected = null
      this.amount = null
      this.note = ""
      this.selectedMethod = null
      this.apiError = null
    },

    async loadMethods() {
      this.methodsLoading = true
      try {
        const res = await axios.get('/back-office/paiement-method/list')
        this.methods = (res.data || []).filter(method => !method.hidden)
      } catch (e) {
        this.apiError = "Impossible de charger les méthodes de paiement."
      } finally {
        this.methodsLoading = false
      }
    },

    async submit() {
      if (!this.canSubmit) return

      this.loading = true
      this.apiError = null
      console.log('im in');
      try {
        await axios.post('/back-office/caisse/errorOrderInvoice', {
          payment_id: this.selected.id, // id de la transaction
          method_id: this.selectedMethod, // new payment method
          original_payment_method_id: this.selected.payment_method_id, // id original
          amount: parseFloat(this.amount),
          note: this.note,
          update_order: false,
        })

        this.closeDialog()
        window.location.reload()
      } catch (e) {
        this.apiError =
          e?.response?.data?.message || "Une erreur est survenue."
      } finally {
        this.loading = false
      }
    },

    formatDate(date) {
      moment.locale('fr')
      return moment(date).format('DD/MM/YYYY HH:mm')
    },

    formatPrice(price) {
      const value = parseFloat(price || 0)
      return value.toFixed(2) + " €"
    },

    getLabel(inv) {
      if (inv.serie) return "Abonnement"
      if (inv.treatment) return "Cure"
      if (inv.custom_discount != 0) return "Remise personnalisée"
      return inv.payment_method?.name || "Paiement"
    }
  }
}
</script>

<style scoped>
.invoice-cancel-modal {
  border-radius: 4px;
  overflow: hidden;
}

.invoice-cancel-modal__header {
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.invoice-cancel-modal__body {
  padding: 1rem;
}

.invoice-cancel-modal__footer {
  padding: .75rem;
  border-top: 1px solid #e9ecef;
}

.invoice-cancel-modal__summary {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

::v-deep .invoice-cancel-slide-y-transition-enter-active,
::v-deep .invoice-cancel-slide-y-transition-leave-active {
  transition: opacity .22s ease, transform .22s ease;
}

::v-deep .invoice-cancel-slide-y-transition-enter,
::v-deep .invoice-cancel-slide-y-transition-leave-to {
  opacity: 0;
  transform: translateY(-32px);
}

::v-deep .invoice-cancel-slide-y-transition-enter-to,
::v-deep .invoice-cancel-slide-y-transition-leave {
  opacity: 1;
  transform: translateY(0);
}
</style>
